import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import './App.css';
import logo from './logo.png';
import Privacy from './Privacy';
import Terms from './Terms';
import CommunityGuidelines from './CommunityGuidelines';
import DateSafety from './DateSafety';
import SmsOptIn from './SmsOptIn';
import DeleteAccount from './DeleteAccount';
import {
  buildLocalizedPath,
  detectBrowserPreference,
  getPreferredLocale,
  I18nProvider,
  persistLocale,
  ROUTES,
  stripLocaleFromPath,
  SUPPORTED_LOCALES,
  normalizeLocale,
  useI18n,
  usePageTitle,
} from './i18n';

const IOS_APP_URL = 'https://apps.apple.com/app/id6748556543';
const IOS_APP_DEEP_LINK = 'itms-apps://apps.apple.com/app/id6748556543';
const ANDROID_PACKAGE_NAME = 'com.sevenloves';

const NAV_ITEMS = [
  { key: 'privacy', path: ROUTES.privacy },
  { key: 'terms', path: ROUTES.terms },
  { key: 'smsOptIn', path: ROUTES.smsOptIn },
  { key: 'guidelines', path: ROUTES.communityGuidelines },
  { key: 'safety', path: ROUTES.dateSafety },
  { key: 'deleteAccount', path: ROUTES.deleteAccount },
];

function getDevicePlatform() {
  if (typeof window === 'undefined') {
    return 'ios';
  }

  const { userAgent = '', platform = '', maxTouchPoints = 0 } = window.navigator;
  const isAndroid = /android/i.test(userAgent);
  const isIOS =
    /iphone|ipad|ipod/i.test(userAgent) ||
    (/mac/i.test(platform) && maxTouchPoints > 1);

  if (isAndroid) {
    return 'android';
  }

  if (isIOS) {
    return 'ios';
  }

  return 'ios';
}

function buildAppStoreUrl(region) {
  if (!region) {
    return IOS_APP_URL;
  }

  return `https://apps.apple.com/${region.toLowerCase()}/app/id6748556543`;
}

function buildGooglePlayUrl(language, region) {
  const searchParams = new URLSearchParams({
    id: ANDROID_PACKAGE_NAME,
  });

  if (language) {
    searchParams.set('hl', language);
  }

  if (region) {
    searchParams.set('gl', region);
  }

  return `https://play.google.com/store/apps/details?${searchParams.toString()}`;
}

function buildGooglePlayDeepLink() {
  return `market://details?id=${ANDROID_PACKAGE_NAME}`;
}

function useStoreUrls() {
  const { locale } = useI18n();
  const browserPreference = detectBrowserPreference();
  const storeLanguage = locale || browserPreference?.language || 'en';
  const storeRegion = browserPreference?.region || null;
  const devicePlatform = getDevicePlatform();
  const appStoreUrl = buildAppStoreUrl(storeRegion);
  const appStoreDeepLink = IOS_APP_DEEP_LINK;
  const googlePlayUrl = buildGooglePlayUrl(storeLanguage, storeRegion);
  const googlePlayDeepLink = buildGooglePlayDeepLink();
  const deepLinkUrl = devicePlatform === 'android' ? googlePlayDeepLink : appStoreDeepLink;

  return {
    appStoreUrl,
    appStoreDeepLink,
    devicePlatform,
    deepLinkUrl,
    googlePlayUrl,
    googlePlayDeepLink,
  };
}

function AppStoreBadge() {
  const { messages } = useI18n();
  const { appStoreUrl } = useStoreUrls();

  return (
    <a
      className="store-badge store-badge-ios"
      href={appStoreUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={messages.store.appStoreAriaLabel}
    >
      <span className="store-badge-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" role="presentation" focusable="false">
          <path
            fill="currentColor"
            d="M16.84 12.71a5.15 5.15 0 0 1 2.45-4.32 5.27 5.27 0 0 0-4.15-2.24c-1.75-.18-3.46 1.05-4.35 1.05-.91 0-2.29-1.03-3.78-1-1.92.03-3.72 1.14-4.7 2.84-2.01 3.48-.51 8.59 1.41 11.39.96 1.37 2.08 2.89 3.55 2.83 1.44-.06 1.98-.92 3.72-.92 1.72 0 2.23.92 3.73.88 1.55-.02 2.52-1.39 3.45-2.78 1.11-1.57 1.56-3.12 1.58-3.2-.03-.01-3.02-1.16-3.05-4.53Zm-2.84-8.39c.78-.95 1.31-2.24 1.16-3.56-1.12.05-2.51.77-3.32 1.7-.72.82-1.36 2.15-1.19 3.42 1.27.09 2.56-.64 3.35-1.56Z"
          />
        </svg>
      </span>
      <span className="store-badge-copy">
        <span className="store-badge-overline">{messages.store.appStoreOverline}</span>
        <span className="store-badge-title">{messages.store.appStoreTitle}</span>
      </span>
    </a>
  );
}

function GooglePlayBadge() {
  const { messages } = useI18n();
  const { googlePlayUrl } = useStoreUrls();

  return (
    <a
      className="store-badge store-badge-android"
      href={googlePlayUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={messages.store.googlePlayAriaLabel}
    >
      <span className="store-badge-icon" aria-hidden="true">
        <svg viewBox="0 0 28 31" role="presentation" focusable="false">
          <path fill="#00D3FF" d="M1.3 1.19 15.95 15.84 1.29 30.5A2.7 2.7 0 0 1 .75 28.9V2.79c0-.58.2-1.13.55-1.6Z" />
          <path fill="#FFCF00" d="m20.75 20.64-3.91-3.91 4.72-4.72 4.62 2.63c1.46.83 1.46 2.92 0 3.75l-5.43 2.25Z" />
          <path fill="#FF3D7F" d="M2.08.66 22.5 12.3l-5.67 5.67L1.3 1.2c.2-.22.47-.4.78-.54Z" />
          <path fill="#34C759" d="M1.29 30.5 16.84 14.95l3.91 3.91L2.08 31.05c-.31-.14-.58-.32-.79-.55Z" />
        </svg>
      </span>
      <span className="store-badge-copy">
        <span className="store-badge-overline">{messages.store.googlePlayOverline}</span>
        <span className="store-badge-title">{messages.store.googlePlayTitle}</span>
      </span>
    </a>
  );
}

function LanguageSwitcher({ onSelect }) {
  const { locale, messages } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const changeLanguage = (nextLocale) => {
    if (nextLocale === locale) {
      return;
    }

    persistLocale(nextLocale);
    const nextPath = buildLocalizedPath(nextLocale, stripLocaleFromPath(location.pathname));
    navigate(`${nextPath}${location.search}${location.hash}`);

    if (onSelect) {
      onSelect();
    }
  };

  return (
    <div className="language-switcher">
      <select
        className="language-select"
        value={locale}
        onChange={(event) => changeLanguage(event.target.value)}
        aria-label={messages.languageSwitcher.label}
      >
        {SUPPORTED_LOCALES.map((languageCode) => (
          <option key={languageCode} value={languageCode}>
            {messages.languageSwitcher.options[languageCode]}
          </option>
        ))}
      </select>
    </div>
  );
}

function Home() {
  const { messages, localizePath } = useI18n();

  usePageTitle(messages.home.metaTitle);

  return (
    <header className="App-header">
      <h1 className="title">{messages.home.title}</h1>
      <p className="subtitle">{messages.home.subtitle}</p>
      <section className="home-copy">
        <p className="home-copy-text">{messages.home.intro}</p>
        <h2 className="home-copy-heading">{messages.home.howItWorks}</h2>
        <p className="home-copy-text">{messages.home.details}</p>
      </section>
      <div className="hero-actions">
        <Link
          className="App-button App-button-primary"
          to={localizePath(ROUTES.download)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {messages.home.primaryCta}
        </Link>
        <a className="App-button App-button-secondary" href="mailto:team@sevenloves.co.uk">
          {messages.home.secondaryCta}
        </a>
      </div>
      <div className="store-links" aria-label={messages.store.groupAriaLabel}>
        <AppStoreBadge />
        <GooglePlayBadge />
      </div>
    </header>
  );
}

function UnknownLocalizedPathRedirect() {
  const { localizePath } = useI18n();

  return <Navigate replace to={localizePath(ROUTES.home)} />;
}

function DownloadPage() {
  const { messages } = useI18n();
  const [modalOpen, setModalOpen] = useState(true);
  const {
    appStoreUrl,
    deepLinkUrl,
    devicePlatform,
    googlePlayUrl,
  } = useStoreUrls();
  const copy = messages.downloadPage;
  const destinationLabel = copy.destinations[devicePlatform] || copy.destinations.ios;

  usePageTitle(copy.metaTitle);

  useEffect(() => {
    if (!modalOpen || typeof window === 'undefined') {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [modalOpen]);

  return (
    <header className="App-header download-page">
      <div className={`download-card${modalOpen ? ' download-card-muted' : ''}`}>
        <h1 className="title download-title">{copy.title}</h1>
        <p className="subtitle download-subtitle">
          {copy.subtitlePrefix} <strong>{destinationLabel}</strong> {copy.subtitleSuffix}
        </p>
        <div className="download-store-grid">
          <a
            className="store-badge store-badge-ios"
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={messages.store.appStoreAriaLabel}
          >
            <span className="store-badge-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                <path
                  fill="currentColor"
                  d="M16.84 12.71a5.15 5.15 0 0 1 2.45-4.32 5.27 5.27 0 0 0-4.15-2.24c-1.75-.18-3.46 1.05-4.35 1.05-.91 0-2.29-1.03-3.78-1-1.92.03-3.72 1.14-4.7 2.84-2.01 3.48-.51 8.59 1.41 11.39.96 1.37 2.08 2.89 3.55 2.83 1.44-.06 1.98-.92 3.72-.92 1.72 0 2.23.92 3.73.88 1.55-.02 2.52-1.39 3.45-2.78 1.11-1.57 1.56-3.12 1.58-3.2-.03-.01-3.02-1.16-3.05-4.53Zm-2.84-8.39c.78-.95 1.31-2.24 1.16-3.56-1.12.05-2.51.77-3.32 1.7-.72.82-1.36 2.15-1.19 3.42 1.27.09 2.56-.64 3.35-1.56Z"
                />
              </svg>
            </span>
            <span className="store-badge-copy">
              <span className="store-badge-overline">{messages.store.appStoreOverline}</span>
              <span className="store-badge-title">{messages.store.appStoreTitle}</span>
            </span>
          </a>
          <a
            className="store-badge store-badge-android"
            href={googlePlayUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={messages.store.googlePlayAriaLabel}
          >
            <span className="store-badge-icon" aria-hidden="true">
              <svg viewBox="0 0 28 31" role="presentation" focusable="false">
                <path fill="#00D3FF" d="M1.3 1.19 15.95 15.84 1.29 30.5A2.7 2.7 0 0 1 .75 28.9V2.79c0-.58.2-1.13.55-1.6Z" />
                <path fill="#FFCF00" d="m20.75 20.64-3.91-3.91 4.72-4.72 4.62 2.63c1.46.83 1.46 2.92 0 3.75l-5.43 2.25Z" />
                <path fill="#FF3D7F" d="M2.08.66 22.5 12.3l-5.67 5.67L1.3 1.2c.2-.22.47-.4.78-.54Z" />
                <path fill="#34C759" d="M1.29 30.5 16.84 14.95l3.91 3.91L2.08 31.05c-.31-.14-.58-.32-.79-.55Z" />
              </svg>
            </span>
            <span className="store-badge-copy">
              <span className="store-badge-overline">{messages.store.googlePlayOverline}</span>
              <span className="store-badge-title">{messages.store.googlePlayTitle}</span>
            </span>
          </a>
        </div>
      </div>
      {modalOpen && (
        <div
          className="download-modal-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setModalOpen(false);
            }
          }}
        >
          <div className="download-modal" role="dialog" aria-modal="true" aria-labelledby="download-modal-title">
            <h2 id="download-modal-title" className="download-modal-title">
              {copy.modalTitle}
            </h2>
            <p className="download-modal-body">
              {copy.modalBodyPrefix} <strong>{destinationLabel}</strong> {copy.modalBodySuffix}
            </p>
            <div className="download-modal-actions">
              <a className="App-button App-button-primary" href={deepLinkUrl}>
                {copy.primaryCta}
              </a>
              <button
                type="button"
                className="App-button App-button-secondary download-modal-dismiss"
                onClick={() => setModalOpen(false)}
              >
                {copy.secondaryCta}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { messages, localizePath } = useI18n();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const closeMenu = () => setMenuOpen(false);

    if (menuOpen) {
      window.addEventListener('resize', closeMenu);
      window.addEventListener('scroll', closeMenu);
      document.addEventListener('click', closeMenu);
    }

    return () => {
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu);
      document.removeEventListener('click', closeMenu);
    };
  }, [menuOpen]);

  const onMenuClick = (event) => event.stopPropagation();

  return (
    <div className="App">
      <nav className="App-nav">
        <Link to={localizePath(ROUTES.home)} className="nav-brand" aria-label={messages.nav.brandAriaLabel}>
          <img src={logo} className="nav-brand-logo" alt="7Loves Logo" />
        </Link>
        <div className="nav-links">
          <NavLink
            to={localizePath(ROUTES.home)}
            end
            className={({ isActive }) => `nav-link${isActive ? ' nav-link-active' : ''}`}
          >
            {messages.nav.home}
          </NavLink>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={localizePath(item.path)}
              className={({ isActive }) => `nav-link${isActive ? ' nav-link-active' : ''}`}
            >
              {messages.nav[item.key]}
            </NavLink>
          ))}
          <a href="mailto:team@sevenloves.co.uk" className="nav-link">
            {messages.nav.support}
          </a>
          <Link
            to={localizePath(ROUTES.download)}
            className="nav-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {messages.nav.download}
          </Link>
          <LanguageSwitcher />
        </div>
        <button
          className="menu-icon"
          onClick={(event) => {
            event.stopPropagation();
            setMenuOpen((currentValue) => !currentValue);
          }}
          aria-label={messages.nav.menuAriaLabel}
          aria-expanded={menuOpen}
          type="button"
        >
          <span className="line"></span>
          <span className="line"></span>
          <span className="line"></span>
        </button>
        {menuOpen && (
          <div className="menu-dropdown" onClick={onMenuClick}>
            <Link to={localizePath(ROUTES.home)} className="dropdown-link">
              {messages.nav.home}
            </Link>
            {NAV_ITEMS.map((item) => (
              <Link key={item.path} to={localizePath(item.path)} className="dropdown-link">
                {messages.nav[item.key]}
              </Link>
            ))}
            <a href="mailto:team@sevenloves.co.uk" className="dropdown-link">
              {messages.nav.support}
            </a>
            <Link
              to={localizePath(ROUTES.download)}
              className="dropdown-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {messages.nav.download}
            </Link>
            <div className="menu-language-row">
              <span className="menu-language-label">{messages.languageSwitcher.label}</span>
              <LanguageSwitcher onSelect={() => setMenuOpen(false)} />
            </div>
          </div>
        )}
      </nav>
      <main className="App-content">
        <Outlet />
      </main>
    </div>
  );
}

function LocalizedLayout() {
  const { locale } = useParams();
  const location = useLocation();
  const normalizedLocale = normalizeLocale(locale);

  if (!normalizedLocale) {
    const preferredLocale = getPreferredLocale();
    const redirectPath = buildLocalizedPath(preferredLocale, stripLocaleFromPath(location.pathname));
    return <Navigate replace to={`${redirectPath}${location.search}${location.hash}`} />;
  }

  if (locale !== normalizedLocale) {
    const redirectPath = buildLocalizedPath(normalizedLocale, stripLocaleFromPath(location.pathname));
    return <Navigate replace to={`${redirectPath}${location.search}${location.hash}`} />;
  }

  return (
    <I18nProvider locale={normalizedLocale}>
      <AppShell />
    </I18nProvider>
  );
}

function LocaleRedirect() {
  const location = useLocation();
  const preferredLocale = getPreferredLocale();
  const redirectPath = buildLocalizedPath(preferredLocale, stripLocaleFromPath(location.pathname));

  return <Navigate replace to={`${redirectPath}${location.search}${location.hash}`} />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path=":locale" element={<LocalizedLayout />}>
          <Route index element={<Home />} />
          <Route path="download" element={<DownloadPage />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="sms-optin" element={<SmsOptIn />} />
          <Route path="community-guidelines" element={<CommunityGuidelines />} />
          <Route path="date-safety" element={<DateSafety />} />
          <Route path="delete-account" element={<DeleteAccount />} />
          <Route path="*" element={<UnknownLocalizedPathRedirect />} />
        </Route>
        <Route path="*" element={<LocaleRedirect />} />
      </Routes>
    </Router>
  );
}

export default App;
